import React from 'react';
import moment from 'moment-timezone';
import type {
  Calendar as CalendarType,
  CalendarEventType,
  Candidate,
  User,
  TimeZone,
} from '@axiom/validation';
import {
  Grid,
  GridColumn,
  GridRow,
  ImageCircle,
  Layout,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Paragraph,
  SmallHeader,
  Calendar,
  CalendarPermissionsUtil,
  Button,
  LayoutItem,
  Gutter,
  TimezoneDropdown,
  Form,
  CalendarTimeZoneUtil,
  useBreakpoint,
  CandidateProfileUtil,
  CalendarEventsUtil,
  EVENT_STATES,
} from '@axiom/ui';
import {
  PossibleImageSizes,
  PermissionImpersonationRoles,
  NO_TIMEZONE,
} from '@axiom/const';

import { CalendarApi } from '../../api/calendar-api';
import { CandidateApi } from '../../api/candidate-api';

import type { CalendarFormType } from './interview-availability-modal-schema';
import { InterviewAvailabilityModalSchema } from './interview-availability-modal-schema';

const dayWeekMode = {
  slots: 4,
  increments: 15,
};

export type InterviewAvailabilityModalProps = {
  candidate: Candidate;
  calendarData: Partial<CalendarType>;
  user: User;
  onClose: () => void;
};

export const InterviewAvailabilityModal = ({
  candidate,
  calendarData,
  user,
  onClose,
}: InterviewAvailabilityModalProps) => {
  const { isMobile } = useBreakpoint();
  const guessIana = moment.tz.guess();
  const { id: calendarId = null, timezone: talentIana } =
    candidate.calendar || {};
  const timezone = CalendarTimeZoneUtil.getTimeZone(talentIana || guessIana);

  const { userPermissions } = CalendarPermissionsUtil(
    user,
    PermissionImpersonationRoles.talent
  );

  const calendarPermissions = userPermissions();

  const initialCalendarEvents = (calendarData.events || []).map(e => {
    return {
      accountName: e.accountName,
      attendees: e.attendees,
      busy: e.busy,
      endTime: e.endTime,
      error: e.error,
      id: e.id,
      name: e.name,
      startTime: e.startTime,
      title: e.title,
      state: e.state ?? EVENT_STATES.SAVED,
    };
  });

  const initValues = {
    CALENDAR_COMPONENT: initialCalendarEvents,
    TIMEZONE_DROPDOWN: timezone === NO_TIMEZONE ? null : timezone.id,
  };

  const handleFormSubmit = async (
    changedData: Partial<CalendarFormType>,
    {
      setFieldError,
    }: { setFieldError: (field: string, message: string) => void },
    { CALENDAR_COMPONENT, TIMEZONE_DROPDOWN }: CalendarFormType
  ) => {
    const cleanEvents = CalendarEventsUtil.cleanAndOrganiseEvents({
      permissions: calendarPermissions,
      initData: initValues.CALENDAR_COMPONENT,
      changedEvents: CALENDAR_COMPONENT,
    });

    const hasCalendarChanges = Object.values(cleanEvents).flat().length > 0;
    const hasTimezoneChange = 'TIMEZONE_DROPDOWN' in changedData;

    if (!hasCalendarChanges && !hasTimezoneChange) {
      // no event changes or timezone changes - require at least one edit
      setFieldError(
        'CALENDAR_COMPONENT',
        'Please make at least one edit to continue.'
      );

      return;
    }

    const { iana }: TimeZone = TIMEZONE_DROPDOWN
      ? CalendarTimeZoneUtil.getZoneFromId(TIMEZONE_DROPDOWN)
      : timezone;

    let currentCalendarId = calendarId;
    if (!currentCalendarId) {
      const newCalendar = await CalendarApi.createCalendar({
        candidateId: candidate.id,
        timezone: iana,
      });
      currentCalendarId = newCalendar.data.id;
    }

    // Deletes must complete before updates/creates to prevent race conditions
    if (cleanEvents.deleteIds.length > 0) {
      await CalendarApi.deleteCalendarEvents(
        currentCalendarId,
        cleanEvents.deleteIds
      );
    }

    // Updates and creates can run in parallel after deletes
    const parallelOps: Array<Promise<unknown>> = [];

    if (cleanEvents.modifiedEvents.length > 0) {
      const payload = cleanEvents.modifiedEvents.reduce<{
        [key: string]: CalendarEventType;
      }>((acc, crnt) => {
        if (crnt.id) {
          acc[crnt.id] = {
            accountName: crnt.accountName,
            attendees: crnt.attendees,
            busy: crnt.busy,
            endTime: crnt.endTime,
            error: crnt.error,
            name: crnt.name,
            startTime: crnt.startTime,
            state: crnt.state,
            title: crnt.title,
          };
        }
        return acc;
      }, {});

      parallelOps.push(
        CalendarApi.updateCalendarEvents(currentCalendarId, payload)
      );
    }

    if (cleanEvents.newEvents.length > 0) {
      parallelOps.push(
        CalendarApi.createCalendarEvents(
          currentCalendarId,
          cleanEvents.newEvents
        )
      );
    }

    await Promise.all(parallelOps);

    if (hasTimezoneChange) {
      // API call not part of the Promise.all() above to avoid race-condition
      await CalendarApi.updateCalendar(currentCalendarId, {
        timezone: iana,
      });
    }

    await Promise.all([
      CalendarApi.refreshCalendar(currentCalendarId),
      CalendarApi.refreshCalendarEvents(currentCalendarId),
    ]);

    // API call not part of the Promise.all() above to avoid race-condition
    await CandidateApi.refreshCandidate(candidate.id);
    // These API calls are in specific order to prevent race-conditions ==================== END

    onClose();
  };

  return (
    <Form
      name="INTERVIEW_AUTO_FORM"
      schema={InterviewAvailabilityModalSchema}
      initialValues={initValues as CalendarFormType}
      onSubmit={handleFormSubmit}
    >
      {({ fireSubmit, values }) => {
        return (
          <Modal size="large" name="INTERVIEW_AUTO_MODAL">
            <ModalHeader name="MODAL_HEADER" onClose={onClose}>
              <Layout position="middle" wrap>
                <LayoutItem rightGutter="16px">
                  <ImageCircle
                    imageName={candidate.calculatedDisplayName}
                    src={CandidateProfileUtil.getProfileImageUri(
                      candidate,
                      PossibleImageSizes.W_100
                    )}
                    size="small"
                    name="TALENT_IMAGE"
                  />
                </LayoutItem>
                <LayoutItem fluid>
                  <SmallHeader name="PENDO_TALENT_INTERVIEW_AVAILABILITY">
                    Update your interview availability
                  </SmallHeader>
                </LayoutItem>
              </Layout>
            </ModalHeader>
            <ModalSection>
              <Grid>
                <GridRow>
                  <GridColumn widescreenWidth={4} smallScreenWidth={12}>
                    <Paragraph>
                      Choose as many time slots as possible to increase the
                      likelihood of being matched for an interview. We suggest a
                      minimum 4 hours over the next two weeks.
                    </Paragraph>
                    <Gutter bottom="24px" />
                    <TimezoneDropdown name="TIMEZONE_DROPDOWN" />
                    <Gutter bottom="16px" />
                  </GridColumn>
                  <GridColumn widescreenWidth={8} smallScreenWidth={12}>
                    <Calendar
                      name="CALENDAR_COMPONENT"
                      permissions={calendarPermissions}
                      dayMode={dayWeekMode}
                      weekMode={dayWeekMode}
                      timezone={
                        values.TIMEZONE_DROPDOWN
                          ? CalendarTimeZoneUtil.getZoneFromId(
                              values.TIMEZONE_DROPDOWN
                            ).iana
                          : timezone.iana
                      }
                    />
                  </GridColumn>
                </GridRow>
              </Grid>
            </ModalSection>
            <ModalFooter>
              <Button
                onClick={() => {
                  onClose();
                }}
                pattern="primary"
                variation="outline"
                name="CANCEL_BUTTON"
              >
                Cancel
              </Button>
              {!isMobile && (
                <Button
                  onClick={() => {
                    void fireSubmit();
                  }}
                  name="UPDATE_BUTTON"
                >
                  Update
                </Button>
              )}
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
